/**
 * Lightweight analytics helper.
 *
 * Pushes events to:
 *  - window.dataLayer (Google Tag Manager / GA4 via GTM)
 *  - window.gtag (GA4 direct, if loaded)
 *  - a `lovable:analytics` CustomEvent on window (for any custom listener)
 *
 * Safe to call even when no analytics script is loaded — it becomes a no-op
 * beyond the console.debug + CustomEvent dispatch, so events are observable
 * during development.
 */

export type AnalyticsParams = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    gtag?: (...args: unknown[]) => void;
  }
}

export function trackEvent(name: string, params: AnalyticsParams = {}): void {
  if (typeof window === "undefined") return;

  const payload = { event: name, ...params, timestamp: Date.now() };

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch {
    /* ignore */
  }

  try {
    window.gtag?.("event", name, params);
  } catch {
    /* ignore */
  }

  try {
    window.dispatchEvent(new CustomEvent("lovable:analytics", { detail: payload }));
  } catch {
    /* ignore */
  }

  if (import.meta.env.DEV) {
    /* eslint-disable no-console */
    console.groupCollapsed(
      `%c[analytics]%c ${name}`,
      "color:#fff;background:#6366f1;padding:2px 6px;border-radius:3px;font-weight:bold;",
      "color:#6366f1;font-weight:bold;"
    );
    console.log("event:", name);
    console.log("params:", params);
    console.log("payload:", payload);
    console.groupEnd();
    /* eslint-enable no-console */
  }
}

/** Convenience wrapper for WhatsApp CTA clicks. */
export function trackWhatsAppClick(source: string, extra: AnalyticsParams = {}): void {
  trackEvent("whatsapp_click", { source, ...extra });
}

/** Convenience wrapper for lead form submissions. */
export function trackLeadSubmit(extra: AnalyticsParams = {}): void {
  trackEvent("lead_submit", extra);
  // Also fire a GA4-style "generate_lead" conversion event.
  trackEvent("generate_lead", extra);
}
